
import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    date: '',
    budget: 50,
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('eventType', formData.eventType);
      formBody.append('date', formData.date);
      formBody.append('budget', `$${formData.budget * 10}`);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5bufv9dn6dhfpiabnfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          date: '',
          budget: 50,
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-3"
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 md:p-12" data-readdy-form id="quick-quote-form">
              <h2 className="font-serif font-bold text-3xl md:text-4xl text-charcoal mb-8">
                Let's Plan Your Surprise
              </h2>

              <div className="space-y-6">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm bg-transparent"
                    placeholder="Your Name"
                  />
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm bg-transparent"
                    placeholder="Email Address"
                  />
                </div>

                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm bg-transparent"
                    placeholder="Phone Number"
                  />
                </div>

                <div className="relative">
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm bg-transparent cursor-pointer"
                  >
                    <option value="">Select Event Type</option>
                    <option value="romantic">Romantic Date</option>
                    <option value="birthday">Birthday</option>
                    <option value="anniversary">Anniversary</option>
                    <option value="proposal">Proposal</option>
                    <option value="justbecause">Just Because</option>
                    <option value="other">Other</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-0 top-3 text-gray-400 pointer-events-none"></i>
                </div>

                <div className="relative">
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm bg-transparent cursor-pointer"
                  />
                  <i className="ri-calendar-line absolute right-0 top-3 text-gray-400 pointer-events-none"></i>
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-3">
                    Budget Range: ${formData.budget * 10}
                  </label>
                  <input
                    type="range"
                    name="budget"
                    min="10"
                    max="100"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, #FF6B6B 0%, #FFB6C1 ${formData.budget}%, #e5e7eb ${formData.budget}%, #e5e7eb 100%)`
                    }}
                  />
                </div>

                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    maxLength={500}
                    rows={4}
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-coral outline-none transition-colors text-sm resize-none bg-transparent"
                    placeholder="Tell us about your vision..."
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-coral to-pink text-white font-semibold rounded-full hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
                >
                  {isSubmitting ? 'Sending...' : 'Get Your Quote'} <i className="ri-sparkling-fill ml-2"></i>
                </button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-green-600 text-sm"
                  >
                    ✓ Thank you! We'll respond within 24 hours.
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-red-600 text-sm"
                  >
                    Something went wrong. Please try again.
                  </motion.div>
                )}

                <div className="text-center text-sm text-gray-500">
                  <i className="ri-time-line mr-1"></i> Response within 24 hours
                </div>
              </div>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-2 space-y-6"
          >
            <div className="w-full h-64 rounded-2xl overflow-hidden mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/5b822d0f9b0a6ed1d6ac705bbeaa0b1d.jpg"
                alt="Celebration decorations"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="bg-cream rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-coral to-pink rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Call Us</div>
                  <div className="font-semibold text-charcoal">(437) 267-2718</div>
                </div>
              </div>
            </div>

            <div className="bg-cream rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple to-pink rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email Us</div>
                  <div className="font-semibold text-charcoal">book.theintimateedit@gmail.com</div>
                </div>
              </div>
            </div>

            <div className="bg-cream rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-coral to-purple rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Availability</div>
                  <div className="font-semibold text-charcoal">Available 24/7</div>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <div className="text-sm text-gray-600 mb-4">Follow Us</div>
              <div className="flex gap-3">
                <a href="https://instagram.com/theintimate.edit" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-charcoal rounded-full flex items-center justify-center text-white hover:bg-coral transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-charcoal rounded-full flex items-center justify-center text-white hover:bg-coral transition-colors cursor-pointer">
                  <i className="ri-tiktok-line"></i>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
